import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API } from '../../App';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { AlertTriangle, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import { apiError } from '../../lib/apiError';

const LABELS = {
  students: 'Students', bookings: 'Bookings', payments: 'Payment records', tests: 'Tests & practice attempts',
  tickets: 'Tickets', expenses: 'Expense & payroll entries', demo_teacher: 'Demo teacher',
};

const PrelaunchResetCard = () => {
  const [status, setStatus] = useState(null);
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    axios.get(`${API}/staff/prelaunch-reset`).then((r) => setStatus(r.data)).catch((e) => toast.error(apiError(e)));
  }, []);
  useEffect(() => { load(); }, [load]);

  const run = async () => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/staff/prelaunch-reset`, { confirm: text });
      toast.success(data.message);
      setText('');
      load();
    } catch (e) {
      toast.error(apiError(e, 'Reset failed'));
    } finally {
      setBusy(false);
    }
  };

  if (!status) return null;

  return (
    <Card className="border-2 border-rose-200 shadow-lg mt-8" data-testid="prelaunch-reset-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-rose-700">
          <AlertTriangle className="w-5 h-5" />Pre-launch reset (one time)
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {status.done ? (
          <p className="flex items-center gap-2 text-emerald-700 font-medium" data-testid="prelaunch-reset-done">
            <CheckCircle2 className="w-5 h-5" />Completed on {new Date(status.done_at).toLocaleString()} by {status.done_by}. This can't be run again.
          </p>
        ) : (
          <>
            <p className="text-sm text-slate-600">
              Permanently deletes all test data. Staff logins, real teacher profiles (with their availability and logins),
              settings and syllabus are kept. This cannot be undone and can only be run once.
            </p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2" data-testid="prelaunch-reset-preview">
              {Object.entries(status.will_delete).map(([k, v]) => (
                <div key={k} className="rounded-lg bg-rose-50 border border-rose-100 px-3 py-2">
                  <p className="text-lg font-bold text-rose-700">{v}</p>
                  <p className="text-xs text-slate-600">{LABELS[k] || k}</p>
                </div>
              ))}
            </div>
            <div className="space-y-1.5">
              <p className="text-sm text-slate-700">Type <span className="font-mono font-semibold">{status.phrase}</span> to confirm</p>
              <div className="flex flex-col sm:flex-row gap-2">
                <Input value={text} onChange={(e) => setText(e.target.value)} placeholder={status.phrase} data-testid="prelaunch-reset-input" />
                <Button onClick={run} disabled={busy || text.trim() !== status.phrase} className="bg-rose-600 hover:bg-rose-700 text-white" data-testid="prelaunch-reset-button">
                  {busy ? 'Deleting...' : 'Delete All Test Data'}
                </Button>
              </div>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default PrelaunchResetCard;
